import * as React from 'react';
import clsx from 'clsx';
import whiteMicrophone from '@/assets/images/svg/white_microphone.svg';
import whiteMicrophoneDisabled from '@/assets/images/svg/white_microphone_disabled.svg';

const InfoTooltip: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  React.useEffect(() => {
    if (open) {
      const timer = setTimeout(() => setOpen(false), 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  return (
    <div
      ref={ref}
      className='relative flex items-center'
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type='button'
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        className='flex size-[14px] items-center justify-center rounded-full bg-gray-350/70 font-proxima text-[8px] font-extrabold lowercase leading-[47px] tracking-normal text-white'
        aria-label='Audio cost info'
      >
        i
      </button>
      {open && (
        <div
          className={clsx(
            'absolute bottom-full left-1/2 z-50 mb-1.5 -translate-x-1/2',
            'whitespace-nowrap bg-gray-660 px-[15px] py-[6px]',
            'rounded-t-[10px] rounded-bl-none rounded-br-[10px]'
          )}
        >
          <span className='font-sans text-10 font-normal leading-[15px] tracking-normal text-white'>
            AUDIO costs{' '}
            <span className='font-sans text-10 font-normal leading-[15px] tracking-normal text-white'>
              2
            </span>{' '}
            extra credits
          </span>
        </div>
      )}
    </div>
  );
};

interface AudioToggleButtonProps {
  isEnabled: boolean;
  onClick: () => void;
  variant?: 'chatHeader';
  className?: string;
}

const SHOW_COST_INFO = false;

export const AudioToggleButton: React.FC<AudioToggleButtonProps> = ({
  isEnabled,
  onClick,
  variant = 'chatHeader',
  className,
}) => {
  const isChatHeader = variant === 'chatHeader';

  return (
    <div className='flex items-center gap-1.5'>
      <button
        type='button'
        onClick={onClick}
        className={clsx(
          'flex items-center justify-center',
          'rounded-[10px]',
          'transition-all duration-200 hover:opacity-90',
          'bg-white/10 text-white hover:bg-white/20',
          isChatHeader && 'gap-1 px-[9px]',
          className
        )}
      >
        <img
          src={isEnabled ? whiteMicrophone : whiteMicrophoneDisabled}
          alt='Audio Call'
          className={clsx('object-contain', isChatHeader && 'h-[11px] w-auto')}
        />
        <span
          className={clsx(
            'font-sans tracking-normal',
            isChatHeader && 'text-[12px] font-normal leading-[27px]'
          )}
        >
          {isEnabled ? 'audio on' : 'audio off'}
        </span>
      </button>
      {SHOW_COST_INFO && <InfoTooltip />}
    </div>
  );
};
